import { css } from '@emotion/react';
import { ObjectTypeCallBlocking } from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { SchemaBlockedNumberService } from '@frontend/api-call-block';
import { Trans, useTranslation } from '@frontend/i18n';
import { useHasPhoneSettingsAccess, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { TextLink, Text, Heading, useModalControl } from '@frontend/design-system';
import { BlockedNumbersTable } from '../../components/call-block/blocked-numbers-table';
import { SeeHistoryButtonWithCallout } from '../../components/common/see-history-button-with-callout';
import { DetailsHeader } from '../../components/phone-system-history/history-details-tray/details-header';
import {
  HISTORY_TRAY_WIDTH,
  HistoryDetailsTray,
  useHistoryTrayShouldOverlap,
} from '../../components/phone-system-history/history-details-tray/history-details-tray';
import { PhoneSystemHistoryTrackingIds } from '../../components/phone-system-history/utils/tracking-ids';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { queryKeys } from '../../query-keys';

export const CallBlockingSettings = () => {
  const { t } = useTranslation('phone');
  const { settingsTenantLocation, associatedLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'associatedLocationIds'
  );
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const { accessibleLocationData } = useAppScopeStore();
  const selectedLocations = (associatedLocationIds ?? []).map((id) => accessibleLocationData[id]).filter(Boolean);
  const { hasPhoneSystemHistoryAccess } = useHasPhoneSettingsAccess({ selectedLocations });

  const [phoneSystemHistoryEnabled] = PhoneConfigOrg.useWetag('phone-system-history');
  const historyTray = useModalControl();
  const shouldOverlapHistoryTray = useHistoryTrayShouldOverlap();
  const isSquishedMode = historyTray.modalProps.show && !shouldOverlapHistoryTray;

  const { data } = useScopedQuery({
    queryKey: [tenantId, ...queryKeys.settings.listCallBlock()],
    queryFn: () =>
      SchemaBlockedNumberService.ListBlockedNumbers({ tenantId: tenantId }).then((res) => res.blockedNumbers),
    meta: {
      error: {
        message: 'CallBlockingSettings:listCallBlockQuery | Failed to fetch blocked numbers',
        data: { tenantId },
      },
    },
  });

  return (
    <>
      <PhoneSettingsPage
        title={t('Call Blocking')}
        subtitle={
          <>
            <Text>{t('Block specific phone numbers that you no longer want to contact your office.')}</Text>
            <Text>
              <Trans t={t}>
                For more information see{' '}
                <TextLink
                  rel='noreferrer'
                  target='_blank'
                  href='https://www.weavehelp.com/weavehelp/article/block-calls'
                >
                  Weave Help
                </TextLink>
                .
              </Trans>
            </Text>
          </>
        }
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Call Blocking'), to: '/phone/call-blocking' },
        ]}
        action={
          phoneSystemHistoryEnabled &&
          hasPhoneSystemHistoryAccess && (
            <SeeHistoryButtonWithCallout
              featureId='phone-system-history-callout-call-blocking'
              isActive={historyTray.modalProps.show}
              onClick={historyTray.modalProps.show ? historyTray.closeModal : historyTray.openModal}
              startDate='2026-07-07'
              endDate='2026-09-22'
              trackingIdPrefix={PhoneSystemHistoryTrackingIds.seeHistoryButtons.callBlocking}
            />
          )
        }
        css={css`
          width: ${isSquishedMode ? `calc(100% - ${HISTORY_TRAY_WIDTH}px)` : '100%'};
          transition: width 0.3s ease;
        `}
      >
        <Heading level={2} css={{ marginBottom: theme.spacing(2) }}>
          {t('Blocked Numbers')}
        </Heading>
        <BlockedNumbersTable blockedNumbers={data ?? []} phoneTenantId={tenantId} />
      </PhoneSettingsPage>

      <HistoryDetailsTray
        modalControls={historyTray}
        tenantId={tenantId}
        callObjectType={ObjectTypeCallBlocking}
        HeaderComponent={<DetailsHeader objectType={ObjectTypeCallBlocking} onClose={historyTray.closeModal} />}
      />
    </>
  );
};
